import { FALLBACK_PRODUCT_IMAGE } from 'constants/Images';
import Image, { ImageProps } from 'next/image';
import { useEffect, useState, forwardRef } from 'react';
import { URL_CDNGCS, URL_GCS_BUYMED, URL_GGCLOUD } from 'sysconfig';
import { convertImageUrl, getImageWithSizeAndQuality, IMAGE_SIZES, shouldReplaceImageUrl } from './ImageUtils';
import shimmer from 'components-v2/atoms/Shimmer';
import { toBase64 } from './StringUtils';

// Lightweight 1x1 pixel base64-encoded image for default blur
const DEFAULT_BLUR_DATA_URL = `data:image/svg+xml;base64,${toBase64(shimmer(42, 42, '#f3f3f3'))}`;

const DEFAULT_FALLBACK_IMAGE = FALLBACK_PRODUCT_IMAGE;

const BE_REPLACED_GIF_URLS = [URL_GGCLOUD];

const ALTERNATIVE_GIF_DOMAINS = [URL_CDNGCS];

const BE_REPLACED_URLS = [URL_GGCLOUD, URL_CDNGCS];

const ALTERNATIVE_DOMAINS = [URL_GCS_BUYMED, URL_CDNGCS]; // Predefined alternative domains

export type ImageSize = keyof typeof IMAGE_SIZES;

type ImageFallbackProps = ImageProps & {
  isGIF?: boolean;
  /**
   * Fallback image URL to use if all other URLs fail.
   */
  fallbackSrc?: string;
  /**
   * Base64-encoded blur placeholder image.
   */
  blurDataURL?: string;
  /**
   * Predefined size for the image.
   * - `thumbnail`: width = 150px, quality = 80
   * - `medium`: width = 300px, quality = 80
   * - `medium_large`: width = 768px, quality = 80
   * - `large`: width = 1024px, quality = 80
   * - `origin`: no width, quality = 100
   */
  size?: ImageSize;
};

export const ImageFallback = forwardRef(
  (
    {
      isGIF = false,
      fallbackSrc,
      blurDataURL = DEFAULT_BLUR_DATA_URL, // Use default lightweight blur image
      size = 'origin',
      ...props
    }: ImageFallbackProps,
    // Nhận nhưng không chuyển tiếp được: next/image v12 là function component nên
    // không ôm được ref. Thiếu tham số này thì React kêu "forwardRef render
    // functions accept exactly two parameters" mỗi lần Fast Refresh dựng lại module.
    _ref,
  ) => {
    let initialUrls = [fallbackSrc || DEFAULT_FALLBACK_IMAGE];

    if (!props.src) {
      return (
        <Image
          {...props}
          src={fallbackSrc || DEFAULT_FALLBACK_IMAGE}
          data-origin-url={props.src}
          onError={() => {}}
          placeholder="blur" // Enable blur effect
          blurDataURL={blurDataURL} // Use custom or default blur placeholder
        />
      );
    }

    initialUrls = ALTERNATIVE_DOMAINS.map((domain) =>
      getImageWithSizeAndQuality(
        shouldReplaceImageUrl(props.src as string, BE_REPLACED_URLS) ? convertImageUrl(props.src as string, domain) : (props.src as string),
        size,
      ),
    );

    if (isGIF) {
      initialUrls = ALTERNATIVE_GIF_DOMAINS.map((domain) =>
        shouldReplaceImageUrl(props.src as string, BE_REPLACED_GIF_URLS) ? convertImageUrl(props.src as string, domain) : (props.src as string),
      );
    }

    const [urls, setUrls] = useState(initialUrls);
    const [src, setSrc] = useState(urls[0]);

    const handleError = () => {
      if (urls.length > 1) {
        // Remove the failed URL and try the next one
        const [, ...remainingUrls] = urls;
        setUrls(remainingUrls);
        setSrc(remainingUrls[0]);
      } else {
        // If no more URLs, use the fallback image
        setSrc(DEFAULT_FALLBACK_IMAGE);
      }
    };

    useEffect(() => {
      // Reset the image source when the src prop changes
      setUrls(initialUrls);
      setSrc(initialUrls[0]);
    }, [props.src]);

    return (
      <Image
        {...props}
        src={src}
        data-origin-url={props.src}
        onError={handleError}
        placeholder="blur" // Enable blur effect
        blurDataURL={blurDataURL} // Use custom or default blur placeholder
      />
    );
  },
);

export const ImageFallbackBanner = ImageFallback;

export const ImageFallbackStatic = ImageFallback;

export const ImageFallbackStoreImage = ImageFallback;

export const ImageFallbackProductImage = ImageFallback;

export default ImageFallback;

